import { doc, getDoc, setDoc } from 'firebase/firestore';
import { firestore } from '../firebase/config';

// Home page layout options
export enum HomePageLayout {
  VIDEO_BACKDROP = 'videoBackdrop',
  INTERACTIVE_3D = 'interactive3D'
}

// Interface for a single background video with trim options
export interface BackgroundVideo {
  id: string;
  url: string;
  title: string;
  startTime: number; // Start time in seconds
  endTime: number | null; // End time in seconds (null means play to the end)
  hasAudio: boolean; // Whether the video has audio
  muted: boolean;
}

// Interface for a tool configuration
export interface ToolConfig {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  enabled: boolean;
  order: number;
  showWatermark?: boolean; // Whether to show a watermark on the tool
  status?: string; // Status label like "Beta", "Alpha", "v1.0", etc.
}

// Interface for power calculator fixtures
export interface PowerFixture {
  id: string;
  name: string;
  wattage: number;
  category: string;
  createdAt: number;
  updatedAt: number;
}

// Interface for lighting database settings
export interface LightingDatabaseSettings {
  enabled: boolean;
  enableUserSubmissions: boolean;
  requireApproval: boolean;
  defaultSortField: string;
  defaultSortDirection: 'asc' | 'desc';
  featuredManufacturers: string[];
  featuredCategories: string[];
}

// Interface for website settings
export interface WebsiteSettings {
  pageVisibility: {
    home: boolean;
    about: boolean;
    services: boolean;
    portfolio: boolean;
    tools: boolean;
    rental: boolean;
    contact: boolean;
  };
  seo: {
    title: string;
    description: string;
    keywords: string;
  };
  social: {
    facebook: string;
    instagram: string;
    youtube: string;
    linkedin: string;
  };
  general: {
    maintenanceMode: boolean;
    showPricing: boolean;
    allowBooking: boolean;
    logoUrl: string;
    homePageLayout: HomePageLayout;
    backgroundVideoUrl: string; // Kept for backward compatibility
    backgroundVideos: BackgroundVideo[]; // New field for multiple videos
    loadingScreen: {
      enabled: boolean;
      transitionDuration: number; // in milliseconds
    };
  };
  tools: {
    [id: string]: ToolConfig;
  };
  powerFixtures: {
    [id: string]: PowerFixture;
  };
  lightingDatabase: LightingDatabaseSettings;
}

// Default settings
export const defaultSettings: WebsiteSettings = {
  pageVisibility: {
    home: true,
    about: true,
    services: true,
    portfolio: true,
    tools: true,
    rental: true,
    contact: true,
  },
  seo: {
    title: 'Paragon Live Design - Professional Audio Visual Services',
    description: 'Paragon Live Design provides professional audio visual services for events, conferences, and productions.',
    keywords: 'audio visual, AV services, event production, sound, lighting, video',
  },
  social: {
    facebook: 'https://facebook.com/paragonlivedesign',
    instagram: 'https://instagram.com/paragonlivedesign',
    youtube: 'https://youtube.com/paragonlivedesign',
    linkedin: 'https://linkedin.com/company/paragonlivedesign',
  },
  general: {
    maintenanceMode: false,
    showPricing: false,
    allowBooking: true,
    logoUrl: '', // Empty string by default to avoid 404 errors
    homePageLayout: HomePageLayout.VIDEO_BACKDROP,
    backgroundVideoUrl: '', // Set to empty string to ensure fallback to hero image works
    backgroundVideos: [
      {
        id: '1',
        url: 'https://assets.mixkit.co/videos/preview/mixkit-set-of-plateaus-seen-from-the-heights-in-a-sunset-26070-large.mp4',
        title: 'Default Video',
        startTime: 0,
        endTime: null,
        hasAudio: false,
        muted: false
      }
    ],
    loadingScreen: {
      enabled: true,
      transitionDuration: 500
    }
  },
  tools: {
    'napkin-cad': {
      id: 'napkin-cad',
      name: 'Napkin CAD',
      description: 'A simple CAD tool for sketching event layouts',
      imageUrl: '',
      enabled: true,
      order: 1,
      showWatermark: true,
      status: 'Beta'
    },
    'power-calculator': {
      id: 'power-calculator',
      name: 'Power Calculator',
      description: 'Calculate amperage and generator requirements for your lighting rig',
      imageUrl: '',
      enabled: true,
      order: 2,
      showWatermark: true, 
      status: 'Beta'
    },
    'lighting-database': {
      id: 'lighting-database',
      name: 'Lighting Fixtures Database',
      description: 'Search and compare professional lighting fixtures from all major manufacturers',
      imageUrl: '',
      enabled: true,
      order: 3,
      showWatermark: true,
      status: 'Alpha'
    }
  },
  powerFixtures: {
    'led-par': {
      id: 'led-par',
      name: 'LED PAR',
      wattage: 150,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'led-moving-head': {
      id: 'led-moving-head',
      name: 'LED Moving Head',
      wattage: 250,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'led-wash': {
      id: 'led-wash',
      name: 'LED Wash',
      wattage: 300,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'source-4-led': {
      id: 'source-4-led',
      name: 'Source 4 LED',
      wattage: 175,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'conventional-par64': {
      id: 'conventional-par64',
      name: 'Conventional PAR64',
      wattage: 1000,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'moving-head-spot': {
      id: 'moving-head-spot',
      name: 'Moving Head Spot',
      wattage: 575,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'fresnel-1k': {
      id: 'fresnel-1k',
      name: 'Fresnel 1K',
      wattage: 1000,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'fresnel-2k': {
      id: 'fresnel-2k',
      name: 'Fresnel 2K',
      wattage: 2000,
      category: 'Lighting',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'small-dimmer-pack': {
      id: 'small-dimmer-pack',
      name: 'Small Dimmer Pack',
      wattage: 2400,
      category: 'Power',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'sound-system-small': {
      id: 'sound-system-small',
      name: 'Sound System (Small)',
      wattage: 1500,
      category: 'Audio',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'sound-system-medium': {
      id: 'sound-system-medium',
      name: 'Sound System (Medium)',
      wattage: 5000,
      category: 'Audio',
      createdAt: Date.now(),
      updatedAt: Date.now()
    },
    'sound-system-large': {
      id: 'sound-system-large',
      name: 'Sound System (Large)',
      wattage: 10000,
      category: 'Audio',
      createdAt: Date.now(),
      updatedAt: Date.now()
    }
  },
  lightingDatabase: {
    enableUserSubmissions: true,
    requireApproval: true,
    defaultSortField: 'name',
    defaultSortDirection: 'asc',
    featuredManufacturers: [],
    featuredCategories: [],
    enabled: true
  }
};

// Helper function to deep merge objects
const deepMerge = (target: any, source: any): any => {
  const output = { ...target };
  
  Object.keys(source).forEach(key => {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (target[key]) {
        output[key] = deepMerge(target[key], source[key]);
      } else {
        output[key] = { ...source[key] };
      }
    } else {
      output[key] = source[key];
    }
  });
  
  return output;
};

const SETTINGS_FETCH_TIMEOUT_MS = 8000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

// Settings service
export const settingsService = {
  async getSettings(): Promise<WebsiteSettings> {
    try {
      const docRef = doc(firestore, 'settings', 'website');
      const docSnap = await withTimeout(getDoc(docRef), SETTINGS_FETCH_TIMEOUT_MS);
      if (!docSnap) {
        console.warn('Settings fetch timed out — using defaults');
        return { ...defaultSettings };
      }
      
      if (docSnap.exists()) {
        const storedSettings = docSnap.data() as Partial<WebsiteSettings>;
        // Deep merge default settings with stored settings
        return deepMerge(defaultSettings, storedSettings);
      }
      
      // If no settings exist, use defaults and save them
      await setDoc(docRef, defaultSettings);
      return { ...defaultSettings };
    } catch (error) {
      console.error('Error fetching settings:', error);
      return { ...defaultSettings };
    }
  },
  
  async updateSettings(settings: Partial<WebsiteSettings>): Promise<WebsiteSettings> {
    try {
      const settingsDoc = doc(firestore, 'settings', 'website');
      
      // Get current settings first
      const snapshot = await getDoc(settingsDoc);
      const currentSettings = snapshot.exists() 
        ? snapshot.data() as WebsiteSettings 
        : defaultSettings;
      
      // Merge with new settings
      const updatedSettings = {
        ...currentSettings,
        ...settings,
        // Handle nested objects
        pageVisibility: {
          ...currentSettings.pageVisibility,
          ...(settings.pageVisibility || {}),
        },
        seo: {
          ...currentSettings.seo,
          ...(settings.seo || {}),
        },
        social: {
          ...currentSettings.social,
          ...(settings.social || {}),
        },
        general: {
          ...currentSettings.general,
          ...(settings.general || {}),
          // Ensure loading screen settings are properly merged
          loadingScreen: {
            ...currentSettings.general.loadingScreen,
            ...(settings.general?.loadingScreen || {}),
          }
        },
        tools: {
          ...currentSettings.tools,
          ...(settings.tools || {}),
        },
        powerFixtures: {
          ...currentSettings.powerFixtures,
          ...(settings.powerFixtures || {}),
        },
        lightingDatabase: {
          ...currentSettings.lightingDatabase,
          ...(settings.lightingDatabase || {}),
        }
      };
      
      await setDoc(settingsDoc, updatedSettings);
      return updatedSettings;
    } catch (error) {
      console.error('Error updating website settings:', error);
      throw error;
    }
  },
  
  async updatePageVisibility(page: keyof WebsiteSettings['pageVisibility'], isVisible: boolean): Promise<WebsiteSettings> {
    try {
      const settings = await this.getSettings();
      settings.pageVisibility[page] = isVisible;
      return await this.updateSettings({ pageVisibility: settings.pageVisibility });
    } catch (error) {
      console.error(`Error updating visibility for ${page}:`, error);
      throw error;
    }
  },

  async updateToolConfig(toolId: string, config: Partial<ToolConfig>): Promise<WebsiteSettings> {
    try {
      const settings = await this.getSettings();
      
      // If tool doesn't exist in settings, initialize it first
      if (!settings.tools[toolId]) {
        settings.tools[toolId] = {
          id: toolId,
          name: config.name || '',
          description: config.description || '',
          imageUrl: config.imageUrl || '',
          enabled: config.enabled !== undefined ? config.enabled : true,
          order: config.order || Object.keys(settings.tools).length + 1
        };
      } else {
        // Update existing tool
        settings.tools[toolId] = {
          ...settings.tools[toolId],
          ...config,
          id: toolId // Ensure id doesn't change
        };
      }
      
      return await this.updateSettings({ tools: settings.tools });
    } catch (error) {
      console.error(`Error updating tool config for ${toolId}:`, error);
      throw error;
    }
  },

  async updatePowerFixture(fixtureId: string, fixture: Partial<PowerFixture>): Promise<WebsiteSettings> {
    try {
      const settings = await this.getSettings();
      
      // If fixture doesn't exist in settings, initialize it first
      if (!settings.powerFixtures[fixtureId]) {
        settings.powerFixtures[fixtureId] = {
          id: fixtureId,
          name: fixture.name || '',
          wattage: fixture.wattage || 0,
          category: fixture.category || 'Lighting',
          createdAt: Date.now(),
          updatedAt: Date.now()
        };
      } else {
        // Update existing fixture
        settings.powerFixtures[fixtureId] = {
          ...settings.powerFixtures[fixtureId],
          ...fixture,
          id: fixtureId, // Ensure id doesn't change
          updatedAt: Date.now()
        };
      }
      
      return await this.updateSettings({ powerFixtures: settings.powerFixtures });
    } catch (error) {
      console.error(`Error updating power fixture config for ${fixtureId}:`, error);
      throw error;
    }
  },
  
  async deletePowerFixture(fixtureId: string): Promise<WebsiteSettings> {
    try {
      const settings = await this.getSettings();
      
      if (settings.powerFixtures[fixtureId]) {
        // Create a copy of the fixtures without the one to be deleted
        const updatedFixtures = { ...settings.powerFixtures };
        delete updatedFixtures[fixtureId];
        
        return await this.updateSettings({ powerFixtures: updatedFixtures });
      }
      
      return settings;
    } catch (error) {
      console.error(`Error deleting power fixture ${fixtureId}:`, error);
      throw error;
    }
  },

  async updateLightingDatabaseSettings(settings: Partial<LightingDatabaseSettings>): Promise<WebsiteSettings> {
    try {
      const currentSettings = await this.getSettings();
      
      const updatedSettings = {
        ...currentSettings,
        lightingDatabase: {
          ...currentSettings.lightingDatabase,
          ...settings
        }
      };
      
      return await this.updateSettings(updatedSettings);
    } catch (error) {
      console.error('Error updating lighting database settings:', error);
      throw error;
    }
  }
}; 